<template>
  <AppDialog
    title="Import members"
    :show="show"
    append-to-body
    @close="close"
  >
    <template #header>
      <SeatsIncreaseWarning
        class="-mr-15 mb-0 mt-10"
        :organization="organization"
        :seats-members-count="seatsMembersCount"
        :charged-members-count="chargedMembersCount"
      />
    </template>

    <AppToggleButtonGroup
      v-if="accountProvisioningVisible"
      :items="items"
      :selected="activeView"
      @input="changeView"
    ></AppToggleButtonGroup>
    <div v-if="showInvites">
      <div
        v-if="activeView === 'accounts'"
        class="mb-15 mt-15"
      >
        <p class="modal-subtitle mb-20">This will create accounts for members automatically.</p>
        <div class="checkbox">
          <AppOnOffToggle
            v-model="allowReceivingEmails"
            class="label-with-tooltip text-lighter"
          >
            <div class="d-flex align-items-center">
              Allow members to receive Hubstaff emails
              <i
                v-tooltip="{ placement: 'top' }"
                class="hi hi-info-filled hi-12 a-tooltip ml-5"
                title="Enabling this setting will allow members to receive emails from Hubstaff about their own work as well as anyone they might manage. You can change this later from the Member settings."
              ></i>
            </div>
          </AppOnOffToggle>
        </div>
      </div>
      <AppFileImport
        :file-types="fileTypes"
        :loading="loading"
        :mime-types="mimeTypes"
        :template-url="templateUrl"
        :resource-type="activeResourceType"
        :organization-id="organizationId"
        :file-params="fileParams"
        :import-config="importConfig"
        @upload-file="upload"
        @remove-file="remove"
        @is-loading="onIsLoading"
        @close="close"
      >
      </AppFileImport>
    </div>
    <div v-else-if="activeView === 'accounts'">
      <CorporateAppUpsell :plan-url="billingOrganizationUrl"></CorporateAppUpsell>
    </div>
  </AppDialog>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { uploadFile, removeFile } from '~/utils/file_utils';
import { mapFormFields } from '~/utils/form_utils';
import { OrganizationsResource } from '~/resources';
import * as types from '../../stores/MembersStore/types';
import AppToggleButtonGroup from '~/components/AppToggleButtonGroup.vue';
import SeatsIncreaseWarning from '~/components/members/SeatsIncreaseWarning.vue';
import {
  INVITE_MEMBER_FILE_IMPORT_TEMPLATE_URL,
  CREATE_ACCOUNT_FILE_IMPORT_TEMPLATE_URL,
  IMPORT_MEMBER_OPTIONS,
} from '~/app_constants';

export default {
  name: 'MembersImportDialog',
  components: {
    AppToggleButtonGroup,
    SeatsIncreaseWarning,
    CorporateAppUpsell: defineAsyncComponent(() => import('./CorporateAppUpsell.vue')),
  },
  props: {
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      items: IMPORT_MEMBER_OPTIONS,
      activeView: 'invites',
    };
  },
  computed: {
    ...mapFormFields(
      'organization',
      {
        allowReceivingEmails: 'allow_receiving_emails',
      },
      'UPDATE_ALLOW_RECEIVING_EMAILS',
    ),
    ...mapState({
      fileParams: 'file_params',
      csvInviteTemplateUrl: 'csv_invite_template_url',
      csvMembersTemplateUrl: 'csv_members_template_url',
      fileImportLoading: 'file_import_loading',
      inviteImportTemplateUrl: 'invite_import_template_url',
      accountImportTemplateUrl: 'account_import_template_url',
      enterprise: 'enterprise_plan',
      seatsMembersCount: 'seats_members_count',
      chargedMembersCount: 'charged_members_count',
      organization: 'organization',
    }),
    ...mapGetters(['organizationId', 'mimeTypes', 'fileTypes', 'accountProvisioningVisible', 'hasFeature']),
    billingOrganizationUrl() {
      return OrganizationsResource.billingLink({ id: this.organizationId });
    },
    loading() {
      return !!this.fileImportLoading;
    },
    accountProvisioningAvailable() {
      return !!(this.organization && this.organization.account_provisioning_available);
    },
    importConfig() {
      if (this.activeView === 'accounts') {
        return { allow_receiving_emails: this.allowReceivingEmails };
      }

      return {};
    },
    templateUrl() {
      if (this.activeView === 'accounts') {
        return this.csvMembersTemplateUrl || CREATE_ACCOUNT_FILE_IMPORT_TEMPLATE_URL;
      }

      return this.csvInviteTemplateUrl || INVITE_MEMBER_FILE_IMPORT_TEMPLATE_URL;
    },
    activeResourceType() {
      if (this.activeView === 'accounts') {
        return 'account';
      }

      return 'member';
    },
    showInvites() {
      return (this.activeView === 'accounts' && this.accountProvisioningAvailable) || this.activeView === 'invites';
    },
  },
  methods: {
    ...mapMutations({
      setLoading: types.SET_UPLOAD_LOADING,
    }),
    close() {
      removeFile(this, types);
      this.$emit('close');
    },
    upload(file) {
      this.setLoading(true);
      uploadFile(this, file, types).catch((e) => {
        this.setLoading(false);
        this.$appMessage(e.message);
      });
    },
    remove() {
      this.setLoading(true);
      removeFile(this, types);
      this.setLoading(false);
    },
    onIsLoading(val) {
      this.setLoading(val);
    },
    changeView() {
      if (this.activeView === 'invites') {
        this.activeView = 'accounts';
      } else {
        this.activeView = 'invites';
      }
    },
  },
};
</script>
